import { type FC } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { Button } from '@/components/atoms/Button'
import { FormControl } from '@/components/atoms/Form/FormControl'
import { FormField } from '@/components/atoms/Form/FormField'
import { FormItem } from '@/components/atoms/Form/FormItem'
import { Icons } from '@/components/atoms/Icons/Icons'
import { TextArea } from '@/components/atoms/TextArea'
import { VoiceMessageButton, VoiceRecorderPanel } from '@/components/molecules/VoiceRecorderPanel'
import { type VoiceRecorder, type VoiceRecording } from '@/hooks/useVoiceRecorder'

import { type TeamMessageFormData } from './TeamMessageFormProvider'

export interface TeamMessageFormProps {
  regionName: string
  disabled?: boolean
  placeholder?: string
  voice?: {
    recorder: VoiceRecorder
    onSend: (blob: Blob, durationMs: number) => Promise<void>
  }
}

export const TeamMessageForm: FC<TeamMessageFormProps> = ({ regionName, disabled, placeholder, voice }) => {
  const intl = useIntl()
  const { isSubmitting } = useFormState<TeamMessageFormData>()
  const message = useWatch<TeamMessageFormData, 'message'>({ name: 'message' })
  const sendVoiceMessage = async ({ blob, durationMs }: VoiceRecording) => {
    await voice?.onSend(blob, durationMs)
  }
  const showVoicePanel = voice && voice.recorder.state.status !== 'idle'
  return (
    <div className="grid grid-cols-[6rem_1fr] items-baseline gap-1 border-t border-gray-200 py-5 text-base">
      <p className=""></p>
      <div className="font-bold select-none">
        {intl.formatMessage({ id: 'message.region' }, { region: regionName })}
      </div>
      <div className="justify-self-center">
        <Icons.ONR />
      </div>
      <div className="self-start pt-2 pr-4">
        {showVoicePanel ? (
          <div className="pb-24">
            <VoiceRecorderPanel recorder={voice.recorder} onSend={sendVoiceMessage} />
          </div>
        ) : (
          <>
            <FormField<TeamMessageFormData, 'message'>
              name="message"
              render={({ field }) => (
                <FormItem className="flex items-center justify-center">
                  <FormControl>
                    <TextArea className="w-full" placeholder={placeholder} disabled={disabled} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex flex-wrap items-center gap-2 pt-6 pb-24">
              <Button type="submit" variant="primary" loading={isSubmitting} disabled={disabled || !message?.trim()}>
                {intl.formatMessage({ id: 'message.action.send' })}
              </Button>
              {voice && <VoiceMessageButton recorder={voice.recorder} disabled={disabled || isSubmitting} />}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
